import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { enGB, lv, type Locale } from 'date-fns/locale';
import { getIntlLocale } from '@/utils/supportedLanguages';

/**
 * The active language expressed the two ways date code needs it.
 *
 * Date formatting was scattered across hardcoded locales — `'en-US'` in the
 * usage charts, `'en-GB'` plus a static `enGB` import in the availability
 * overrides, bare `format()` calls with no locale in the calendar — so
 * weekdays and month names stayed English however the app was set. One hook
 * means a formatter can no longer be written without a locale by accident.
 */
export interface DateLocale {
  /** For date-fns `format`, `parse` and friends. */
  dateFns: Locale;
  /** For `Intl.*` and `toLocaleDateString` — a BCP 47 tag, e.g. `lv-LV`. */
  intl: string;
}

const DATE_FNS_LOCALES: Record<string, Locale> = {
  lv_LV: lv,
  en: enGB,
};

export function useDateLocale(): DateLocale {
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? i18n.language;

  return useMemo(
    () => ({
      // enGB rather than enUS: the product is European and the rest of the app
      // already formatted dates day-first.
      dateFns: DATE_FNS_LOCALES[language] ?? enGB,
      intl: getIntlLocale(language),
    }),
    [language]
  );
}

export default useDateLocale;
